import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/other-hazardous-waste-page.module.css'
// eslint-disable-next-line import/extensions
import hazardousWaste from '../../json/other-hazardous-waste-data.json'

const hazardousServiceData = {
  name: 'Hazardous Waste Disposal',
  description:
    'Comprehensive hazardous waste removal for businesses. Dimension is the catch-all for chemical waste — solvents, acetone, drums, corrosives, oils, and mixed loads — with DOT/RCRA-compliant transport and full manifesting.',
  serviceType: 'Hazardous Waste Disposal',
  url: 'https://getdimension.com/other-hazardous-waste',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Chemical and Solvent Waste Disposal' },
    { name: 'Drum and Tote Hazardous Waste Removal' },
    { name: 'Mixed and Unclassified Hazardous Waste Disposal' },
    { name: 'Corrosive and Reactive Liquid Disposal' },
    { name: 'Used and Waste Oil Disposal' },
  ],
}

const hazardousFAQData = {
  questions: hazardousWaste.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function OtherHazardousWaste() {
  return (
    <>
      <Head>
        <title>Hazardous Waste Disposal for Businesses | Dimension</title>
        <meta
          name="description"
          content="Comprehensive hazardous waste disposal for businesses — solvents, chemicals, drums, corrosives, oils, and mixed loads. DOT/RCRA compliant, full manifesting, instant quote, 30% less than competitor rates."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/other-hazardous-waste" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={hazardousServiceData} />
        <SchemaMarkup type="FAQPage" data={hazardousFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heading={hazardousWaste.pitch.heading}
          material={hazardousWaste.pitch.material}
          pitchText={hazardousWaste.pitch.pitchText}
          ctaLink={hazardousWaste.pitch.ctaLink}
          ctaText={hazardousWaste.pitch.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...hazardousWaste.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...hazardousWaste.benefits} />
        <CaseStudy {...hazardousWaste.caseStudy} />
        <Approach {...hazardousWaste.approach} />
        <FAQ {...hazardousWaste.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Hazardous Waste Disposal',
              href: '/hazardous-waste-disposal',
              description:
                'The hub for every hazardous stream — what counts as hazardous waste, how the process works, and which service you need.',
            },
            {
              label: 'Regulated Waste Disposal',
              href: '/regulated-waste-disposal',
              description:
                'Universal waste, batteries, aerosols, antifreeze, gases, and lab packs with full manifesting.',
            },
            {
              label: 'Flammable Waste Disposal',
              href: '/flammable-waste-disposal',
              description:
                'Compliant disposal of solvents, used fuel, aerosols, and other DOT Class 3 ignitable waste.',
            },
            {
              label: 'Corrosive Waste Disposal',
              href: '/corrosive-waste-disposal',
              description:
                'Safe disposal of acids, caustics, and other corrosive liquids for businesses nationwide.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default OtherHazardousWaste
